"use client";

import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Caveat } from "next/font/google";
import { useRouter } from "next/navigation";
import Image from "next/image";
import Link from "next/link";
import { industryData, START_FORM_INDUSTRIES } from "./odooIndustriesData";
export { industryData, START_FORM_INDUSTRIES };

const caveat = Caveat({ subsets: ["latin"], weight: ["400", "700"], display: "swap" });
const MotionLink = motion.create ? motion.create(Link) : motion(Link);

const IndustryCardIcon = ({ item }) => {
  const [imgSrc, setImgSrc] = useState(`/odoo/icons/industries/${item.icon}.png`);

  useEffect(() => {
    setImgSrc(`/odoo/icons/industries/${item.icon}.png`);
  }, [item.icon]);

  return (
    <Image
      src={imgSrc}
      alt={item.title}
      width={64}
      height={64}
      className="w-full h-full object-contain"
      onError={() => {
        setImgSrc("/odoo/icons/green_thumbs-up.svg");
      }}
    />
  );
};


/* ─────────────────────────────────────
   Trial / Advisor Form Modal
   ───────────────────────────────────── */
function IndustryModal({ industry, onClose }) {
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", company: "", employees: "", message: "" });
  const [imgSrc, setImgSrc] = useState(`/odoo/icons/industries/${industry.icon}.png`);

  useEffect(() => {
    setImgSrc(`/odoo/icons/industries/${industry.icon}.png`);
  }, [industry.icon]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        {/* Backdrop */}
        <motion.div
          className="absolute inset-0 bg-black/70 backdrop-blur-sm"
          onClick={onClose}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        />

        {/* Modal */}
        <motion.div
          className="relative w-full max-w-lg bg-[#0f0f13] border border-white/10 rounded-3xl shadow-[0_30px_80px_rgba(0,0,0,0.7)] overflow-hidden z-10"
          initial={{ opacity: 0, scale: 0.92, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.92, y: 30 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
        >
          {/* Top gradient bar */}
          <div className="h-1 w-full bg-gradient-to-r from-[#f1833b] via-[#ff6b35] to-[#f1833b]" />

          <div className="p-8">
            {!submitted ? (
              <>
                {/* Header */}
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-white rounded-2xl p-2.5 flex-shrink-0 shadow-lg relative">
                      <Image
                        src={imgSrc}
                        alt={industry.title}
                        width={56}
                        height={56}
                        className="w-full h-full object-contain"
                        onError={() => { setImgSrc("/odoo/icons/green_thumbs-up.svg"); }}
                      />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white leading-tight">{industry.title}</h3>
                      <p className="text-sm text-gray-400 mt-0.5">{industry.desc}</p>
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="text-gray-500 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10 ml-2 flex-shrink-0"
                    aria-label="Close"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="h-px w-full bg-white/10 mb-6" />

                <div className="mb-6">
                  <h4 className={`text-2xl font-bold text-white mb-1 ${caveat.className}`}>
                    Get started with Odoo for <span className="text-[#f1833b]">{industry.title}</span>
                  </h4>
                  <p className="text-sm text-gray-400">Fill in your details and our team will reach out to help you get set up.</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Full Name *</label>
                      <input
                        required
                        type="text"
                        placeholder="John Smith"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-gray-600 outline-none focus:border-[#f1833b]/50 focus:bg-white/8 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Work Email *</label>
                      <input
                        required
                        type="email"
                        placeholder="you@company.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-gray-600 outline-none focus:border-[#f1833b]/50 focus:bg-white/8 transition-all"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Company Name</label>
                      <input
                        type="text"
                        placeholder="Acme Corp"
                        value={formData.company}
                        onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-gray-600 outline-none focus:border-[#f1833b]/50 focus:bg-white/8 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Company Size</label>
                      <select
                        value={formData.employees}
                        onChange={(e) => setFormData({ ...formData, employees: e.target.value })}
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#f1833b]/50 transition-all appearance-none"
                        style={{ color: formData.employees ? 'white' : '#4b5563', backgroundColor: 'rgba(255,255,255,0.05)' }}
                      >
                        <option value="" disabled style={{ backgroundColor: '#1a1a2e', color: '#9ca3af' }}>Select size</option>
                        <option value="1-5" style={{ backgroundColor: '#1a1a2e', color: 'white' }}>1–5 employees</option>
                        <option value="6-50" style={{ backgroundColor: '#1a1a2e', color: 'white' }}>6–50 employees</option>
                        <option value="51-250" style={{ backgroundColor: '#1a1a2e', color: 'white' }}>51–250 employees</option>
                        <option value="250+" style={{ backgroundColor: '#1a1a2e', color: 'white' }}>250+ employees</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Tell us about your needs</label>
                    <textarea
                      rows={3}
                      placeholder={`What are you looking to achieve with Odoo for your ${industry.title}?`}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-gray-600 outline-none focus:border-[#f1833b]/50 focus:bg-white/8 transition-all resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full py-3.5 bg-gradient-to-r from-[#f1833b] to-[#e8711f] hover:from-[#ff9451] hover:to-[#f1833b] text-white font-bold rounded-xl transition-all shadow-lg shadow-[#f1833b]/20 hover:shadow-[#f1833b]/30 hover:scale-[1.01] active:scale-[0.99]"
                  >
                    Start Free Trial →
                  </button>

                  <p className="text-center text-xs text-gray-500">No credit card required · Free 15-day trial · Cancel anytime</p>
                </form>
              </>
            ) : (
              /* Success State */
              <motion.div
                className="text-center py-8"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
              >
                <div className="w-20 h-20 bg-gradient-to-br from-green-400/20 to-green-600/20 border border-green-500/30 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">You're all set! 🎉</h3>
                <p className="text-gray-400 text-sm mb-6">
                  Thanks, <strong className="text-white">{formData.name || "there"}</strong>! Our team will reach out to <strong className="text-white">{formData.email}</strong> within 24 hours to help you get started with <strong className="text-[#f1833b]">{industry.title}</strong>.
                </p>
                <button
                  onClick={onClose}
                  className="px-8 py-3 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-xl transition-all border border-white/10"
                >
                  Close
                </button>
              </motion.div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

/* ─────────────────────────────────────
   Main OdooIndustries Component
   ───────────────────────────────────── */
export default function OdooIndustries() {
  const [searchQuery, setSearchQuery] = useState("");
  const router = useRouter();

  const filteredData = industryData.map(cat => ({
    ...cat,
    items: cat.items.filter(item =>
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.desc.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(cat => cat.items.length > 0);

  const handleCardClick = (item) => {
    if (START_FORM_INDUSTRIES.has(item.id)) {
      // Open the trial start form with clean URL path
      router.push(`/odoo/start/${item.id}`);
    } else {
      // All other industries → go to their industry page
      router.push(`/odoo/industries/${item.id}`);
    }
  };

  return (
    <>
      <section className="py-24 px-6 sm:px-12 lg:px-24 relative z-10 font-sans overflow-hidden">
        {/* Subtle Background Glows */}
        <div className="absolute top-1/4 left-0 w-96 h-96 bg-[#f1833b]/10 rounded-full blur-[120px] -z-10" />
        <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-[#003366]/20 rounded-full blur-[120px] -z-10" />

        <div className="container mx-auto">

          {/* Header & Search */}
          <div className="text-center mb-16">
            <motion.h2
              className={`text-5xl md:text-7xl font-bold text-white mb-8 tracking-tight ${caveat.className}`}
            >
              Choose your <span className="relative inline-block">
                <span className="relative z-10 text-[#f1833b]">Industry</span>
                <svg className="absolute -bottom 1 left-0 w-full h-16 -z-0 opacity-80 scale-y-125 origin-top" viewBox="0 0 200 100" preserveAspectRatio="none">
                  <path
                    d="m1.78,98.974c-2.734-3.693-2.319-7.014,1.247-9.961,3.566-2.948,9.479-4.841,17.739-5.681,1.828-.062,3.597-.086,5.306-.074,1.828-.062,3.201-.289,4.118-.682.973-.068,3.509-.212,7.608-.432,4.305-.169,9.421-.394,15.347-.676,6.014-.156,12.25-.361,18.707-.614,6.544-.127,12.661-.258,18.351-.391,5.808-.208,10.643-.347,14.505-.419,3.861-.072,5.998-.056,6.41.047.23.576.875.893,1.936.951,1.265.109,2.358-.033,3.275-.426,1.124-.342,2.437-.532,3.941-.571,1.59.087,2.679.307,3.265.661.767-.119,1.638-.213,2.611-.281,1.061.058,1.871.001,2.433-.17.562-.171,1.152-.179,1.769-.025.736.08,1.386.035,1.947-.136,1.765.338,4.342.619,7.728.843,3.506.15,7.44.304,11.8.461,4.448.282,8.912.465,13.391.547,4.566.208,8.823.339,12.772.393,3.949.054,7.265.053,9.948-.003,2.682-.055,4.304-.169,4.865-.339,2.041-.735,3.625.076,4.752,2.433.91.331,1.725,1.262,2.448,2.792,0,0-.209.311-.628.933-.213.673-.608,1.82-1.184,3.44-.457,1.546-1.262,2.59-2.417,3.131-1.067.667-2.385,1.22-3.951,1.658-1.716.712-3.386,1.125-5.008,1.238-1.504.039-3.83-.128-6.98-.501-3.031-.447-7.8-1.069-14.305-1.866-1.592-.087-3.225-.236-4.903-.448-1.473-.161-2.885-.358-4.238-.593-1.149-.183-2.149-.278-3.004-.284-.737-.08-1.223-.046-1.461.102-.562.171-1.578.176-3.05.015-1.266-.109-2.814-.133-4.641-.071-1.354-.235-3.297-.461-5.829-.68-2.533-.218-5.168-.462-7.906-.732-2.651-.144-5.038-.274-7.158-.389-2.003-.19-3.124-.21-3.36-.062-.443.097-.989.168-1.638.213-.562.171-1.239.053-2.029-.352-.232-.576-.789-.768-1.675-.574-.679.245-1.02.367-1.02.367-.949.593-4.989.776-12.12.549-3.331.101-8.328.252-14.991.453-6.576.327-13.903.674-21.982,1.04-7.873.418-15.644.861-23.312,1.33-7.549.395-14.081.785-19.596,1.169-5.428.51-8.882,1.047-10.361,1.611-3.077,1.202-5.161,1.874-6.253,2.016-1.211.216-2.283-.104-3.218-.96Z"
                    fill="#f1833b"
                  />
                </svg>
              </span>
            </motion.h2>

            <div className="max-w-3xl mx-auto relative group">
              <div className="absolute inset-0 bg-[#f1833b]/5 rounded-2xl blur-xl group-hover:bg-[#f1833b]/10 transition-all" />
              <div className="relative flex items-center bg-white/5 border border-white/10 rounded-2xl shadow-2xl hover:border-white/20 transition-all px-6 py-1 backdrop-blur-md">
                <input
                  type="text"
                  placeholder="Search for an Industry (e.g. Accounting, Retail...)"
                  className="w-full py-4 text-lg text-white placeholder-gray-500  bg-transparent border-none outline-none focus:ring-0"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <span className="text-2xl text-[#f1833b] ml-4">
                  <i className="fas fa-search"></i>
                </span>
              </div>
            </div>
          </div>

          {/* Categories & Items */}
          <div className="space-y-24">
            <AnimatePresence mode="popLayout">
              {filteredData.length > 0 ? (
                filteredData.map((category, catIdx) => (
                  <motion.div
                    key={category.category}
                    exit={{ opacity: 0, scale: 0.95 }}
                    layout
                  >
                    <div className="flex items-center gap-4 mb-10">
                      <h3 className={`text-4xl font-bold text-white tracking-tight ${caveat.className}`}>
                        {category.category}
                      </h3>
                      <div className="h-[1px] flex-grow bg-white/10" />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {category.items.map((item, itemIdx) => {
                        const isBuilt = !START_FORM_INDUSTRIES.has(item.id);
                        const cardHref = isBuilt
                          ? `/odoo/industries/${item.id}`
                          : `/odoo/start/${item.id}`;
                        return (
                          <MotionLink
                            key={item.id}
                            href={cardHref}
                            transition={{ delay: itemIdx * 0.02 }}
                            whileHover={{ y: -8, backgroundColor: "rgba(255,255,255,0.08)" }}
                            className="group relative bg-white/5 border border-white/10 p-7 rounded-3xl transition-all cursor-pointer shadow-xl backdrop-blur-sm h-full block"
                          >
                            <div className="flex items-center gap-5">
                              <div className="w-16 h-16 bg-white rounded-2xl p-3 flex-shrink-0 group-hover:scale-110 transition-transform shadow-lg shadow-black/20">
                                <IndustryCardIcon item={item} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="text-lg font-bold text-white mb-1 group-hover:text-[#f1833b] transition-colors leading-tight">
                                  {item.title}
                                </h4>
                                <p className="text-gray-400 text-xs leading-relaxed line-clamp-2">
                                  {item.desc}
                                </p>
                              </div>
                              {/* Indicator: arrow for pages, form icon for modal */}
                              <div className="flex-shrink-0 ml-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                {isBuilt ? (
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f1833b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M5 12h14M12 5l7 7-7 7" />
                                  </svg>
                                ) : (
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f1833b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18M9 21V9" />
                                  </svg>
                                )}
                              </div>
                            </div>
                          </MotionLink>
                        );
                      })}
                    </div>
                  </motion.div>
                ))
              ) : (
                <motion.div
                  className="text-center py-32 bg-white/5 rounded-[40px] border-2 border-dashed border-white/10"
                >
                  <div className="text-6xl mb-6">🔍</div>
                  <h4 className="text-2xl font-bold text-white">No results found.</h4>
                  <p className="text-gray-400 mt-3 max-w-md mx-auto">
                    We couldn't find any industries matching "{searchQuery}".
                  </p>
                  <button
                    onClick={() => setSearchQuery("")}
                    className="mt-8 px-8 py-3 bg-[#f1833b] text-black font-bold rounded-full hover:bg-white transition-all shadow-lg"
                  >
                    Reset search
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

        </div>
      </section>
    </>
  );
}
